(() => {
  'use strict';

  // A section script can be included again by the theme editor.
  const runtimeKey = '__bozbroLpV2';
  if (window[runtimeKey]) {
    window[runtimeKey].scan(document);
    return;
  }

  const storageKey = 'bb-coupon-v2-dismissed';
  const instances = new Map();
  const lockedDialogs = new Set();
  let bodyStyles;
  let dismissed = false;
  let productIntent = false;
  try { dismissed = sessionStorage.getItem(storageKey) === '1'; } catch (_) { /* Storage is optional. */ }

  function suppressAuto(hideNudges = true) {
    dismissed = true;
    try { sessionStorage.setItem(storageKey, '1'); } catch (_) { /* Keep the in-memory suppression. */ }
    instances.forEach(instance => instance.stopAuto(hideNudges));
  }

  function lockBody(dialog) {
    if (lockedDialogs.has(dialog)) return;
    if (!lockedDialogs.size) {
      const style = document.body.style;
      bodyStyles = ['overflow', 'padding-right'].map(property => [property, style.getPropertyValue(property), style.getPropertyPriority(property)]);
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      if (scrollbar) style.setProperty('padding-right', `${(parseFloat(getComputedStyle(document.body).paddingRight) || 0) + scrollbar}px`);
      style.setProperty('overflow', 'hidden');
    }
    lockedDialogs.add(dialog);
  }

  function unlockBody(dialog) {
    lockedDialogs.delete(dialog);
    if (!lockedDialogs.size && bodyStyles) {
      bodyStyles.forEach(([property, value, priority]) => {
        if (value) document.body.style.setProperty(property, value, priority);
        else document.body.style.removeProperty(property);
      });
      bodyStyles = undefined;
    }
  }

  function init(root) {
    if (instances.has(root)) return;
    let destroyed = false;
    const cleanups = [];
    const on = (target, event, handler, options) => {
      if (!target?.addEventListener) return;
      target.addEventListener(event, handler, options);
      cleanups.push(() => target.removeEventListener(event, handler, options));
    };
    const watchMedia = (query, handler) => {
      if (query.addEventListener) on(query, 'change', handler);
      else { query.addListener(handler); cleanups.push(() => query.removeListener(handler)); }
    };
    const placement = (element, fallback) => {
      const value = element?.dataset.bbPlacement || element?.dataset.bbCta || fallback;
      return /^[a-z0-9_-]{1,40}$/i.test(value || '') ? value : fallback;
    };
    const emit = (action, where) => {
      if (!destroyed) root.dispatchEvent(new CustomEvent('bozbro:interaction', { bubbles: true, detail: { action, placement: where } }));
    };

    const hero = root.querySelector('.bb-hero');
    const sticky = root.querySelector('[data-bb-sticky]');
    const menu = root.querySelector('dialog[data-bb-menu]');
    const coupon = root.querySelector('dialog[data-bb-coupon]');
    const nudge = root.querySelector('[data-bb-coupon-nudge]');
    const desktop = matchMedia('(min-width: 641px) and (hover: hover) and (pointer: fine)');
    const mobileVideo = matchMedia('(max-width: 640px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const originalHidden = new Map([sticky, nudge, menu, coupon].filter(Boolean).map(element => [element, element.hidden]));
    const openDialogs = new Map();
    let activeDialog = null;
    let autoTimer = null;
    let visibleMilliseconds = 0;
    let visibleSince = document.hidden ? null : performance.now();
    let maxScroll = 0;

    const isEditing = () => Boolean(window.Shopify?.designMode || root.dataset.designMode === 'true');
    const autoEnabled = () => root.dataset.autoCoupon === 'true' && !isEditing();
    const isFieldFocused = () => Boolean(document.activeElement?.matches('input, textarea, select') || document.activeElement?.isContentEditable);
    const hasDialog = () => Boolean(document.querySelector('dialog[open]'));
    const hideNudge = () => { if (nudge) nudge.hidden = true; };
    const stopAuto = (hide = true) => {
      clearTimeout(autoTimer);
      autoTimer = null;
      if (hide) hideNudge();
    };
    const inlineCouponVisible = () => {
      const band = root.querySelector('.bb-line-band');
      if (!band) return false;
      const rect = band.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < innerHeight;
    };
    const elapsed = () => visibleMilliseconds + (visibleSince === null ? 0 : performance.now() - visibleSince);
    const canAutoShow = () => !destroyed && autoEnabled() && !dismissed && !productIntent && !document.hidden && !isFieldFocused() && !hasDialog() && !inlineCouponVisible();

    function setExpanded(dialog, expanded) {
      const selector = dialog === menu ? '[data-bb-menu-open]' : '[data-bb-coupon-open]';
      root.querySelectorAll(selector).forEach(trigger => trigger.setAttribute('aria-expanded', String(expanded)));
    }

    function finishClose(dialog, restoreFocus = true, report = true) {
      const state = openDialogs.get(dialog);
      if (!state) { unlockBody(dialog); return; }
      openDialogs.delete(dialog);
      if (activeDialog === dialog) activeDialog = null;
      unlockBody(dialog);
      setExpanded(dialog, false);
      if (dialog === coupon) suppressAuto();
      if (report) emit(dialog === menu ? 'menu_close' : 'coupon_close', state.placement);
      if (restoreFocus && !hasDialog()) {
        let target = state.trigger;
        // A dismissed nudge's opener is hidden; use a visible equivalent control.
        if (!target?.isConnected || !target.getClientRects().length) {
          target = [...root.querySelectorAll('[data-bb-coupon-open], [data-bb-menu-open]')].find(element => {
            const rect = element.getBoundingClientRect();
            return element.getClientRects().length && rect.bottom > 0 && rect.top < innerHeight;
          });
        }
        target?.focus({ preventScroll: true });
      }
    }

    function closeDialog(dialog = activeDialog, restoreFocus = true, report = true) {
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      finishClose(dialog, restoreFocus, report);
    }

    function openDialog(dialog, trigger, where) {
      if (!dialog || destroyed) return false;
      if (dialog === activeDialog && dialog.open) return true;
      let returnTrigger = trigger || document.activeElement;
      // A coupon opened from the menu must return to the visible menu opener.
      if (activeDialog?.contains(returnTrigger)) returnTrigger = openDialogs.get(activeDialog)?.trigger || returnTrigger;
      // Only close dialogs owned by this LP. Never dismiss another app's modal.
      if ([...document.querySelectorAll('dialog[open]')].some(other => !lockedDialogs.has(other))) return false;
      instances.forEach(instance => instance.closeDialog(undefined, false));
      dialog.hidden = false;
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else { dialog.setAttribute('open', ''); dialog.setAttribute('aria-modal', 'true'); }
      } catch (_) { return false; }
      activeDialog = dialog;
      openDialogs.set(dialog, { trigger: returnTrigger, placement: where });
      lockBody(dialog);
      setExpanded(dialog, true);
      hideNudge();
      if (dialog === coupon) suppressAuto();
      emit(dialog === menu ? 'menu_open' : 'coupon_open', where);
      if (!dialog.contains(document.activeElement)) {
        const target = dialog.querySelector('[autofocus], button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        (target || dialog).focus({ preventScroll: true });
      }
      return true;
    }

    [menu, coupon].filter(Boolean).forEach(dialog => {
      let downOutside = false;
      const outside = event => {
        const rect = dialog.getBoundingClientRect();
        return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      };
      on(dialog, 'pointerdown', event => { downOutside = event.target === dialog && outside(event); });
      on(dialog, 'click', event => {
        if (downOutside && event.target === dialog && outside(event)) closeDialog(dialog);
        downOutside = false;
      });
      on(dialog, 'cancel', event => { event.preventDefault(); closeDialog(dialog); });
      on(dialog, 'close', () => { if (!dialog.open) finishClose(dialog); });
    });

    on(document, 'keydown', event => {
      if (!activeDialog?.open) return;
      if (event.key === 'Escape') { event.preventDefault(); closeDialog(); return; }
      // Native dialog traps focus; this also supports the small non-native fallback.
      if (event.key === 'Tab') {
        const items = [...activeDialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')].filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) { event.preventDefault(); activeDialog.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !activeDialog.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !activeDialog.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    });

    function updateAuto() {
      if (destroyed) return;
      if (!autoEnabled() || productIntent || desktop.matches || inlineCouponVisible()) hideNudge();
      if (!desktop.matches && nudge && elapsed() >= 45000 && maxScroll >= 0.4 && canAutoShow()) {
        nudge.hidden = false;
        suppressAuto(false);
        emit('coupon_nudge_show', 'mobile_nudge');
      }
    }

    function scheduleAuto() {
      clearTimeout(autoTimer);
      autoTimer = null;
      if (destroyed || !autoEnabled() || dismissed || productIntent || document.hidden) { updateAuto(); return; }
      const wait = (desktop.matches ? 25000 : 45000) - elapsed();
      if (wait > 0) autoTimer = setTimeout(() => { autoTimer = null; updateAuto(); }, wait + 20);
      updateAuto();
    }

    function updateScroll() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0) maxScroll = Math.max(maxScroll, Math.min(1, Math.max(0, window.scrollY) / scrollable));
      updateAuto();
    }

    on(window, 'scroll', updateScroll, { passive: true });
    on(document.documentElement, 'mouseleave', event => {
      if (event.clientY <= 0 && desktop.matches && elapsed() >= 25000 && maxScroll >= 0.2 && canAutoShow()) {
        openDialog(coupon, document.activeElement, 'exit_intent');
      }
    });
    on(document, 'focusin', () => { if (isFieldFocused() || hasDialog()) hideNudge(); });
    on(document, 'focusout', () => { queueMicrotask(() => { if (!destroyed) updateAuto(); }); });
    watchMedia(desktop, scheduleAuto);
    if ('MutationObserver' in window) {
      const observer = new MutationObserver(scheduleAuto);
      observer.observe(root, { attributes: true, attributeFilter: ['data-auto-coupon', 'data-design-mode'] });
      cleanups.push(() => observer.disconnect());
    }

    // Preserve campaign values only on the actual store, including its own preview domain.
    const incoming = new URLSearchParams(window.location.search);
    const allowed = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    function withCampaign(href) {
      if (!href || href.startsWith('#')) return href;
      let target;
      try { target = new URL(href, location.href); } catch (_) { return href; }
      if (!['http:', 'https:'].includes(target.protocol)) return href;
      const ownPreviewStore = target.origin === location.origin && /\.myshopify\.com$/i.test(location.hostname) && /\/(?:products|collections)\/[^/]+/.test(target.pathname);
      if (target.hostname !== 'bozbro.jp' && !ownPreviewStore) return href;
      for (const key of allowed) {
        if (incoming.has(key) && incoming.get(key).length <= 256) target.searchParams.set(key, incoming.get(key));
      }
      return target.href;
    }

    root.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      const updated = withCampaign(href);
      if (updated && updated !== href) link.href = updated;
    });

    const video = root.querySelector('video[data-bb-video]');
    const videoToggle = root.querySelector('[data-bb-video-toggle]');
    const videoLabel = videoToggle?.querySelector('[data-bb-video-label]') || videoToggle;
    let heroVisible = !hero || (hero.getBoundingClientRect().bottom > 0 && hero.getBoundingClientRect().top < innerHeight);
    let userPaused = false;
    let manualVideo = false;
    let loadedSource = '';
    let playEpoch = 0;
    let playPending = false;
    let failedSource = '';

    const limitedVideo = () => reduced.matches || Boolean(connection?.saveData);
    const wantsVideo = () => video && !destroyed && heroVisible && !document.hidden && !userPaused && (manualVideo || !limitedVideo());
    const videoSource = () => mobileVideo.matches ? (video?.dataset.mobile || video?.dataset.desktop) : (video?.dataset.desktop || video?.dataset.mobile);
    function videoState(playing) {
      root.classList.toggle('bb-video-playing', playing);
      if (videoToggle) {
        const label = playing ? '動画を停止' : '動画を再生';
        videoLabel.textContent = label;
        videoToggle.setAttribute('aria-label', label);
        videoToggle.setAttribute('aria-pressed', String(playing));
      }
    }

    function pauseVideo() {
      if (!video) return;
      playEpoch += 1;
      playPending = false;
      video.pause();
      videoState(false);
    }

    function unloadVideo() {
      if (!video) return;
      pauseVideo();
      if (video.hasAttribute('src')) { video.removeAttribute('src'); video.load(); }
      loadedSource = '';
    }

    function syncVideo() {
      if (!video) return;
      if (!wantsVideo()) { pauseVideo(); return; }
      const source = videoSource();
      if (!source || source === failedSource) { pauseVideo(); return; }
      if (loadedSource !== source) {
        pauseVideo();
        loadedSource = source;
        video.src = source;
        video.load();
      }
      if (!video.paused || playPending) return;
      const epoch = ++playEpoch;
      playPending = true;
      let result;
      try { result = video.play(); } catch (_) { result = Promise.reject(new Error('Video playback unavailable')); }
      Promise.resolve(result).then(() => {
        if (destroyed || epoch !== playEpoch) return;
        playPending = false;
        if (!wantsVideo()) pauseVideo();
        else videoState(!video.paused);
      }).catch(() => {
        if (destroyed || epoch !== playEpoch) return;
        playPending = false;
        failedSource = source;
        videoState(false);
      });
    }

    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      // JS owns autoplay so reduced-motion, data-saving and offscreen states win.
      video.autoplay = false;
      video.preload = 'none';
      videoState(false);
      on(video, 'playing', () => { if (wantsVideo()) videoState(true); else pauseVideo(); });
      on(video, 'pause', () => { if (video.paused) videoState(false); });
      on(video, 'error', () => { failedSource = loadedSource; pauseVideo(); });
      watchMedia(mobileVideo, syncVideo);
      const preferencesChanged = () => {
        manualVideo = false;
        failedSource = '';
        if (limitedVideo()) unloadVideo();
        else syncVideo();
      };
      watchMedia(reduced, preferencesChanged);
      on(connection, 'change', preferencesChanged);
      if (limitedVideo()) unloadVideo();
    }

    if (hero && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        heroVisible = entries[0].isIntersecting && entries[0].intersectionRatio > 0;
        if (sticky) sticky.hidden = heroVisible;
        syncVideo();
      }, { threshold: 0 });
      observer.observe(hero);
      cleanups.push(() => observer.disconnect());
    } else if (hero) {
      const updateHero = () => {
        const rect = hero.getBoundingClientRect();
        heroVisible = rect.bottom > 0 && rect.top < innerHeight;
        if (sticky) sticky.hidden = heroVisible;
        syncVideo();
      };
      on(window, 'scroll', updateHero, { passive: true });
      on(window, 'resize', updateHero, { passive: true });
      updateHero();
    }

    on(document, 'visibilitychange', () => {
      if (document.hidden) {
        if (visibleSince !== null) visibleMilliseconds += performance.now() - visibleSince;
        visibleSince = null;
        clearTimeout(autoTimer);
        autoTimer = null;
        pauseVideo();
      } else {
        visibleSince = performance.now();
        scheduleAuto();
        syncVideo();
      }
    });

    on(root, 'click', event => {
      const element = event.target instanceof Element ? event.target.closest('a, button, [data-bb-close], [data-bb-menu-open], [data-bb-coupon-open], [data-bb-nudge-close], [data-bb-video-toggle], [data-bb-cta], [data-bb-line]') : null;
      if (!element || !root.contains(element)) return;
      if (element.matches('[data-bb-close]')) { event.preventDefault(); closeDialog(element.closest('dialog')); return; }
      if (element.matches('[data-bb-menu-open]')) { event.preventDefault(); openDialog(menu, element, placement(element, 'header')); return; }
      if (element.matches('[data-bb-coupon-open]')) { event.preventDefault(); openDialog(coupon, element, placement(element, 'manual')); return; }
      if (element.matches('[data-bb-nudge-close]')) { event.preventDefault(); suppressAuto(); emit('coupon_nudge_close', 'mobile_nudge'); return; }
      if (element.matches('[data-bb-video-toggle]') && video) {
        event.preventDefault();
        if ((!video.paused && !video.ended) || playPending) {
          userPaused = true;
          manualVideo = false;
          pauseVideo();
          emit('video_pause', placement(element, 'hero'));
        } else {
          userPaused = false;
          manualVideo = true;
          failedSource = '';
          syncVideo();
          emit('video_play', placement(element, 'hero'));
        }
        return;
      }
      if (element.matches('[data-bb-line]')) {
        suppressAuto();
        emit('coupon_line_click', placement(element, activeDialog === coupon ? 'coupon' : 'line'));
        closeDialog();
        return;
      }
      let productLink = element.dataset.bbCta === 'product';
      if (element.matches('a[href]')) {
        try { productLink ||= /\/products\/[^/]+/.test(new URL(element.href, location.href).pathname); } catch (_) { /* Not a URL CTA. */ }
      }
      if (productLink) { productIntent = true; instances.forEach(instance => instance.stopAuto()); }
      if (productLink || element.hasAttribute('data-bb-cta')) emit('cta_click', placement(element, productLink ? 'product' : 'cta'));
      if (element.matches('a[href]') && activeDialog?.contains(element)) closeDialog();
    });

    const instance = {
      stopAuto,
      closeDialog,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        stopAuto();
        // Remove callbacks before closing/loading elements during teardown.
        cleanups.reverse().forEach(cleanup => cleanup());
        [...openDialogs.keys()].forEach(dialog => closeDialog(dialog, false, false));
        unloadVideo();
        originalHidden.forEach((hidden, element) => { element.hidden = hidden; });
        root.classList.remove('bb-video-playing');
        instances.delete(root);
        delete root.bbCleanup;
      }
    };
    instances.set(root, instance);
    root.bbCleanup = () => instance.destroy();
    hideNudge();
    updateScroll();
    scheduleAuto();
    syncVideo();
  }

  function rootsWithin(scope) {
    const roots = scope.matches?.('[data-bb-lp]') ? [scope] : [];
    return roots.concat([...(scope.querySelectorAll?.('[data-bb-lp]') || [])]);
  }
  function scan(scope) { rootsWithin(scope).forEach(init); }
  window[runtimeKey] = { scan };
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => rootsWithin(event.target).forEach(root => instances.get(root)?.destroy()));
  window.addEventListener('pagehide', () => [...instances.values()].forEach(instance => instance.destroy()));
  window.addEventListener('pageshow', () => scan(document));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
